import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { AppModal } from './common/AppModal';
import { InstallmentEditor, validateInstallmentPct } from './paymentTerms/InstallmentEditor';
import {
  startSoModification,
  previewSoModification,
  submitSoModification,
  executeSoModification,
  resolveSoModificationGst,
  cancelSoModification,
  getPaymentTerm,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { dispositionTaxableAmount } from '../utils/helpers';
import { prepareWizardInstallments, isSubscriptionStyleBilling } from '../utils/installmentScheduleUtils';

const CATCHUP_METHODS = [
  { value: 'SPREAD_EVENLY', label: 'Spread evenly across future periods' },
  { value: 'IMMEDIATE', label: 'Immediate correction (current month)' },
];

/**
 * V2 SO modification wizard — impact preview, catch-up method, submit/execute.
 */
export default function SoModificationWizard({ po, terms, onClose, onSaved, catchupApplicable = false }) {
  const { isHeadAccounts, isFinance, isAdmin } = useAuth();
  const canResolveGst = isHeadAccounts || isFinance || isAdmin;
  const [step, setStep] = useState(1);
  const [modificationId, setModificationId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catchupMethod, setCatchupMethod] = useState('SPREAD_EVENLY');
  const [form, setForm] = useState({
    title: po.title || '',
    description: po.description || '',
    po_value: po.po_value || '',
    currency: po.currency || 'INR',
    service_period_from: '',
    service_period_to: '',
    change_comment: '',
  });
  const [installments, setInstallments] = useState([]);
  const [termForm, setTermForm] = useState({ name: '', term_type: 'DELIVERY_BASED' });
  const submittedRef = useRef(false);
  const primaryTerm = terms[0];

  const subscriptionStyle = isSubscriptionStyleBilling(installments);
  const servicePeriodSet = Boolean(form.service_period_from && form.service_period_to);
  const usesServicePeriodFilter = subscriptionStyle && servicePeriodSet;

  const effectiveInstallments = prepareWizardInstallments(
    installments,
    form.service_period_from,
    form.service_period_to
  );

  const editorInstallments = usesServicePeriodFilter ? effectiveInstallments : installments;

  const handleSetInstallments = (updater) => {
    if (!usesServicePeriodFilter) {
      setInstallments(updater);
      return;
    }
    setInstallments((prev) => {
      const current = prepareWizardInstallments(
        prev,
        form.service_period_from,
        form.service_period_to
      );
      const next = typeof updater === 'function' ? updater(current) : updater;
      return next.map((inst, i) => ({ ...inst, sequence_no: i + 1 }));
    });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!primaryTerm?.term_id) return;
    getPaymentTerm(primaryTerm.term_id).then(res => {
      const t = res.data.term;
      setTermForm({ name: t.name, term_type: t.term_type });
      setInstallments((t.installments || []).map(i => ({
        sequence_no: i.sequence_no,
        label: i.label,
        amount_type: i.amount_type,
        amount_value: i.amount_value,
        trigger_type: i.trigger_type,
        anchor_date_type: i.anchor_date_type,
        due_offset_days: i.due_offset_days || 0,
        scheduled_invoice_date: i.scheduled_invoice_date
          ? String(i.scheduled_invoice_date).split('T')[0] : '',
        grace_period_days: i.grace_period_days || 0,
        requires_buyer_approval: i.requires_buyer_approval || false,
      })));
    }).catch(() => {});
  }, [primaryTerm?.term_id]);

  const buildNewSoParameters = () => ({
    title: form.title,
    description: form.description,
    po_value: parseFloat(form.po_value),
    currency: form.currency,
    service_period_from: form.service_period_from || undefined,
    service_period_to: form.service_period_to || undefined,
    payment_term: primaryTerm ? {
      name: termForm.name,
      term_type: termForm.term_type,
      installments: editorInstallments,
    } : undefined,
  });

  const handleClose = async () => {
    if (modificationId && !submittedRef.current) {
      try {
        await cancelSoModification(modificationId);
      } catch {
        // Draft may already be superseded or discarded
      }
    }
    onClose();
  };

  const handleStartAndPreview = async (e) => {
    e.preventDefault();
    if (!form.change_comment?.trim()) {
      return toast.error('Change comment is required for audit trail');
    }
    if (!form.service_period_from || !form.service_period_to) {
      return toast.error('New service period (from and to) is required');
    }
    if (new Date(form.service_period_to) <= new Date(form.service_period_from)) {
      return toast.error('Service period end must be after start');
    }
    if (editorInstallments.length > 0 && !validateInstallmentPct(editorInstallments)) {
      return toast.error('Installments must sum to 100%');
    }
    setLoading(true);
    try {
      let modId = modificationId;
      if (!modId) {
        const startRes = await startSoModification({
          old_po_id: po.po_id,
          reason: form.change_comment,
        });
        modId = startRes.data.modification_id;
        setModificationId(modId);
      }
      const previewRes = await previewSoModification(modId, {
        new_so_parameters: buildNewSoParameters(),
        catchup_method: catchupMethod,
      });
      setPreview(previewRes.data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to compute impact preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (catchupMethod === 'IMMEDIATE' && !(preview?.catchup?.catchup_amount > 0.01)) {
      return toast.error(
        'IMMEDIATE catch-up requires catchup_amount > 0 in preview. ' +
        'Widen/narrow the new service period so POSTED months fall outside it (e.g. Apr–Dec after Jan–Mar posted).'
      );
    }
    setLoading(true);
    try {
      const submitRes = await submitSoModification(modificationId);
      if (submitRes.data.status !== 'APPROVED') {
        submittedRef.current = true;
        toast.success(
          'Submitted for Head Accounts approval. Credit notes and O2C sync run only after approval and execution — see the banner on the SO page.',
          { duration: 6000 }
        );
        onSaved();
        onClose();
        return;
      }
      try {
        submittedRef.current = true;
        const execRes = await executeSoModification(modificationId);
        toast.success(execRes.data.message || 'SO modification executed');
        onSaved(execRes.data.new_po?.po_id);
      } catch (execErr) {
        toast.error(execErr.response?.data?.error || 'Failed to execute modification');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit modification');
    } finally {
      setLoading(false);
    }
  };

  const showCatchup = catchupApplicable || preview?.catchup_applicable;

  const dispositionRows = (preview?.disposition?.filter(
    d => d.action_taken !== 'CARRIED_FORWARD'
  ) || []).filter((d) => {
    if (showCatchup) return true;
    return !['REVENUE_RECOGNITION_ENTRY', 'UNBILLED_REVENUE'].includes(d.artifact_type);
  });

  return (
    <AppModal
      title={`Modify ${po.po_number} — new SO version`}
      onClose={handleClose}
      maxWidth={900}
    >
      {step === 1 && (
        <form onSubmit={handleStartAndPreview} className="space-y-4">
          <p className="text-sm text-gray-600">
            Substantive commercial changes create a new SO version with per-artifact disposition
            {catchupApplicable
              ? ' (cancel / reverse / credit note / revenue catch-up).'
              : ' (cancel / credit note on billed amounts). Revenue catch-up applies only after revenue or unbilled entries have been posted.'}
          </p>
          <div>
            <label className="label">Change comment <span className="text-red-500">*</span></label>
            <input className="input" required value={form.change_comment}
              onChange={e => set('change_comment', e.target.value)}
              placeholder="Why is this SO being modified?" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">New SO Value</label>
              <input type="number" step="0.01" className="input" required
                value={form.po_value} onChange={e => set('po_value', e.target.value)} />
            </div>
            <div>
              <label className="label">Currency</label>
              <input className="input" value={form.currency} disabled />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">New service period from *</label>
              <input type="date" className="input" required value={form.service_period_from}
                onChange={e => set('service_period_from', e.target.value)} />
            </div>
            <div>
              <label className="label">New service period to *</label>
              <input type="date" className="input" required value={form.service_period_to}
                onChange={e => set('service_period_to', e.target.value)}
                min={form.service_period_from} />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Set the <strong>new</strong> revenue recognition window for the modified SO version.
            Keep the same dates if only the SO value changes.
            {catchupApplicable && (
              <> Narrow the window when posted months should park and catch up on the new schedule.</>
            )}
            {' '}A different window than the billed invoice may trigger a full credit note on that invoice.
          </p>
          {catchupApplicable && (
          <div>
            <label className="label">Revenue catch-up method</label>
            <select className="select" value={catchupMethod}
              onChange={e => setCatchupMethod(e.target.value)}>
              {CATCHUP_METHODS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          )}
          {primaryTerm && (editorInstallments.length > 0 || usesServicePeriodFilter) && (
            <div className="border rounded-xl p-4">
              <div className="text-sm font-medium mb-2">
                Installments (new version billing)
                {usesServicePeriodFilter && editorInstallments.length < installments.length && (
                  <span className="text-gray-500 font-normal ml-2">
                    — {editorInstallments.length} months in service period
                  </span>
                )}
              </div>
              <InstallmentEditor
                installments={editorInstallments}
                setInstallments={handleSetInstallments}
                currency={form.currency}
                maxHeight="30vh"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Computing…' : 'Preview impact'}
            </button>
          </div>
        </form>
      )}

      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500">Already billed</div>
              <div className="font-semibold">
                {preview.already_billed?.already_billed?.toLocaleString()} {form.currency}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500">New SO value</div>
              <div className="font-semibold">{preview.new_po_value?.toLocaleString()} {form.currency}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-gray-500">New billing total</div>
              <div className="font-semibold text-blue-800">
                {preview.new_version_billing_total?.toLocaleString()} {form.currency}
              </div>
            </div>
          </div>

          {preview.gst_manual_review_required && (
            <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex items-center justify-between gap-3">
              <span>GST manual review required — credit notes outside statutory window need Head Accounts resolution before approval.</span>
              {canResolveGst && modificationId && (
                <button
                  type="button"
                  className="btn-secondary text-xs whitespace-nowrap"
                  disabled={loading}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await resolveSoModificationGst(modificationId);
                      toast.success('GST manual review marked resolved');
                      const res = await previewSoModification(modificationId, {
                        new_so_parameters: buildNewSoParameters(),
                        catchup_method: catchupMethod,
                      });
                      setPreview(res.data);
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Failed to resolve GST review');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Mark GST resolved
                </button>
              )}
            </div>
          )}

          <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Artifact</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {dispositionRows.length === 0 ? (
                  <tr><td colSpan={3} className="p-3 text-gray-500">No dispositions required</td></tr>
                ) : dispositionRows.map((d, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-2">{d.artifact_type}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        d.action_taken.includes('CREDIT') || d.action_taken.includes('DEBIT')
                          ? 'bg-orange-100 text-orange-800'
                          : d.action_taken === 'PARKED'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100'
                      }`}>
                        {d.action_taken}
                      </span>
                    </td>
                    <td className="p-2 text-right">
                      {d.amount != null ? dispositionTaxableAmount(d).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showCatchup && catchupMethod === 'IMMEDIATE' && !(preview.catchup?.catchup_amount > 0.01) && (
            <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              IMMEDIATE catch-up amount is zero — submitting would skip catch-up journals on post.
              Set service period so already-POSTED months (e.g. Jan–Mar) are outside the new window.
            </div>
          )}

          {showCatchup && preview.catchup?.catchup_amount != null && Math.abs(preview.catchup.catchup_amount) > 0.01 && (
            <div className="text-sm text-gray-700 space-y-2">
              <div>
                Revenue catch-up: <strong>{preview.catchup.catchup_amount.toLocaleString()}</strong>
                {' '}via {catchupMethod.replace('_', ' ').toLowerCase()}
                {preview.catchup_preview?.needs_manual_resolution && (
                  <span className="text-amber-600"> — may need manual resolution</span>
                )}
              </div>
              {preview.catchup_preview?.periods?.length > 0 && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        {['Month', 'Normal Revenue', 'Catch-up', 'Revenue Posted'].map(h => (
                          <th key={h} className="p-2 text-left font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.catchup_preview.periods.map(p => (
                        <tr key={p.period_month} className="border-t">
                          <td className="p-2">{p.period_month?.slice(0, 7)}</td>
                          <td className="p-2">{Number(p.scheduled_amount).toLocaleString()}</td>
                          <td className="p-2 text-violet-700">
                            {p.reduction > 0.009 ? `−${Number(p.reduction).toLocaleString()}` : '—'}
                          </td>
                          <td className="p-2 font-medium">{Number(p.adjusted_amount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>Back</button>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" onClick={handleClose}>Cancel</button>
              <button type="button" className="btn-primary" disabled={loading || preview.blocked}
                onClick={handleSubmit}>
                {loading ? 'Submitting…' : (
                  preview.requires_approval ? 'Submit for approval' : 'Execute modification'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppModal>
  );
}
